import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  CircularProgress,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Delete, Edit, Add } from "@mui/icons-material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useDispatch } from "react-redux";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";
import { showSnackbar } from "../../store/snackbarSlice/snackbarSlice";

const DiscountEntries = () => {
  const dispatch = useDispatch();

  const controllerUrl = "/discount-entries";
  const schedulesControllerUrl = "/discount-entry-schedules";

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  const [form, setForm] = useState({
    description: "",
    days_of_week: "ALWAYS_SAME",
    working_mode: "",
    using_condition: "",
    max_stay_time: "",
    inadverted_access_tolerance: "",
    habilitate_excess: "",
    excess_description: "",
  });

  const [errors, setErrors] = useState({});

  // --- SCHEDULES STATE ---
  const [schedules, setSchedules] = useState([]);
  const [schedulesLoading, setSchedulesLoading] = useState(false);

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [scheduleForm, setScheduleForm] = useState({
    weekday: "",
    start_time: "",
    end_time: "",
    debit_percentage: "",
    monthly_unlimited: false,
    monthly_max_per_zone: "",
    monthly_days_per_zone: "",
    monthly_max: "",
    monthly_days: "",
    daily_unlimited: false,
    daily_max_per_zone: "",
    daily_days_per_zone: "",
    daily_max: "",
    daily_days: "",
    usage_interval: "",
    group_by: "NONE",
  });
  const [scheduleErrors, setScheduleErrors] = useState({});

  // --- ENUM OPTIONS (front-end) ---

  const daysOfWeekOptions = [
    { value: "ALWAYS_SAME", label: "Sempre igual" },
    { value: "DIFFERENCES_BETWEEN_DAYS", label: "Diferenças entre dias" },
  ];

  const workingModeOptions = [
    {
      value: "DEBIT_BASED_HOUR_OF_ENTRY",
      label: "Débito baseado na hora de entrada",
    },
    {
      value: "DEBIT_ACCORDING_TO_HOURS",
      label: "Débito consoante as horas",
    },
    {
      value: "INSTALATIONS_ENTRY_HOUR",
      label: "Hora de entrada nas instalações",
    },
  ];

  // TODO: trocar pelos valores reais de ConditionsOfUsage
  const usingConditionOptions = [
    { value: "ALWAYS", label: "Sempre" },
    { value: "CONDITIONED_BY_PRESENCES", label: "Condicionado pela Presença" },
  ];

  const habilitateExcessOptions = [
    { value: "IN_USAGE_ENTRY_LINES", label: "Nas linhas de utilização" },
    { value: "LAUNCH_AS_DEBT", label: "Lançar como dívida" },
    { value: "DONT_ALLOW", label: "Não permitir" },
  ];

  const weekdayOptions = [
    { value: 0, label: "Domingo" },
    { value: 1, label: "Segunda" },
    { value: 2, label: "Terça" },
    { value: 3, label: "Quarta" },
    { value: 4, label: "Quinta" },
    { value: 5, label: "Sexta" },
    { value: 6, label: "Sábado" },
  ];

  const groupByOptions = [
    { value: "NONE", label: "Não agrupar" },
    { value: "BY_INVENTORY", label: "Por inventário" },
  ];

  // --- DERIVED UI LOGIC ---

  const requiresStayFields = useMemo(
    () =>
      form.working_mode === "DEBIT_BASED_HOUR_OF_ENTRY" ||
      form.working_mode === "DEBIT_ACCORDING_TO_HOURS",
    [form.working_mode]
  );

  const requiresExcessDescription = useMemo(
    () => form.habilitate_excess === "LAUNCH_AS_DEBT",
    [form.habilitate_excess]
  );

  const schedulesUseWeekday =
    selectedEntry?.days_of_week === "DIFFERENCES_BETWEEN_DAYS";

  // --- MODAL HANDLERS ---

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setSelectedEntry(null);
    setForm({
      description: "",
      days_of_week: "ALWAYS_SAME",
      working_mode: "",
      using_condition: "",
      max_stay_time: "",
      inadverted_access_tolerance: "",
      habilitate_excess: "",
      excess_description: "",
    });
    setErrors({});
    setSchedules([]);
    setSchedulesLoading(false);
  };

  // --- LOAD DATA ---

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await callBackendApi(controllerUrl, "GET");
      setEntries(response.data || []);
    } catch (e) {
      console.error(e);
      dispatch(showSnackbar({ message: "Erro ao carregar Discount Entries." }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // --- LOAD SCHEDULES FOR ENTRY ---

  const loadSchedules = async (discountEntryId) => {
    try {
      setSchedulesLoading(true);
      const response = await callBackendApi(
        `${schedulesControllerUrl}?discount_entry_id=${discountEntryId}`,
        "GET"
      );
      setSchedules(response.data || []);
    } catch (e) {
      console.error(e);
      dispatch(
        showSnackbar({ message: "Erro ao carregar horários desta regra." })
      );
    } finally {
      setSchedulesLoading(false);
    }
  };

  // --- CREATE ENTRY MODAL ---

  const createEntryModal = () => {
    setSelectedEntry(null);
    setForm({
      description: "",
      days_of_week: "ALWAYS_SAME",
      working_mode: "",
      using_condition: "",
      max_stay_time: "",
      inadverted_access_tolerance: "",
      habilitate_excess: "",
      excess_description: "",
    });
    setErrors({});
    setSchedules([]);
    handleOpen();
  };

  // --- EDIT ENTRY MODAL ---

  const updateEntryModal = (id) => {
    const found = entries.find((e) => e.id === id);

    if (!found) {
      return dispatch(showSnackbar({ message: "Item não encontrado." }));
    }

    setSelectedEntry(found);
    setForm({
      description: found.description ?? "",
      days_of_week: found.days_of_week ?? "ALWAYS_SAME",
      working_mode: found.working_mode ?? "",
      using_condition: found.using_condition ?? "",
      max_stay_time: found.max_stay_time ?? "",
      inadverted_access_tolerance: found.inadverted_access_tolerance ?? "",
      habilitate_excess: found.habilitate_excess ?? "",
      excess_description: found.excess_description ?? "",
    });
    setErrors({});
    setSchedules([]);
    handleOpen();
    loadSchedules(found.id);
  };

  // --- SAVE ENTRY (CREATE/UPDATE) ---

  const saveEntry = async () => {
    setErrors({});

    const payload = {
      ...form,
      max_stay_time:
        form.max_stay_time === "" || !requiresStayFields
          ? null
          : Number(form.max_stay_time),
      inadverted_access_tolerance:
        form.inadverted_access_tolerance === "" || !requiresStayFields
          ? null
          : Number(form.inadverted_access_tolerance),
      excess_description:
        form.excess_description === "" ? null : form.excess_description,
    };

    try {
      const url = selectedEntry
        ? `${controllerUrl}/${selectedEntry.id}`
        : controllerUrl;
      const method = selectedEntry ? "PUT" : "POST";

      const response = await callBackendApi(url, method, payload);

      if (response && response.success) {
        dispatch(showSnackbar({ message: "Guardado com sucesso!" }));
        handleClose();
        loadData();
      } else if (response && response.error) {
        setErrors(response.error);
        dispatch(
          showSnackbar({
            message: "Existem erros de validação. Verifica o formulário.",
          })
        );
      } else {
        dispatch(
          showSnackbar({
            message: "Ocorreu um erro ao guardar.",
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: "Erro no pedido." }));
    }
  };

  // --- DELETE ENTRY ---

  const handleDelete = async (id) => {
    try {
      const confirmDelete = window.confirm(
        "Tens a certeza que queres eliminar este registo?"
      );
      if (!confirmDelete) return;

      const response = await callBackendApi(`${controllerUrl}/${id}`, "DELETE");

      if (response && response.success) {
        dispatch(showSnackbar({ message: "Eliminado com sucesso!" }));
        loadData();
      } else {
        dispatch(
          showSnackbar({
            message: "Ocorreu um erro ao eliminar.",
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: "Erro no pedido." }));
    }
  };

  // --- ENTRY TABLE COLUMNS ---

  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "description", headerName: "Descrição", flex: 1 },
    {
      field: "days_of_week",
      headerName: "Tipo Dias",
      width: 180,
      valueFormatter: (params) => {
        if (params.value === "ALWAYS_SAME") return "Sempre igual";
        if (params.value === "DIFFERENCES_BETWEEN_DAYS")
          return "Diferenças entre dias";
        return params.value;
      },
    },
    {
      field: "working_mode",
      headerName: "Modo de Trabalho",
      width: 220,
    },
    {
      field: "habilitate_excess",
      headerName: "Excesso",
      width: 180,
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => updateEntryModal(params.row.id)}
            color="primary"
            size="small"
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => handleDelete(params.row.id)}
            color="error"
            size="small"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  // --- SCHEDULE MODAL HELPERS ---

  const resetScheduleForm = () => {
    setScheduleForm({
      weekday: "",
      start_time: "",
      end_time: "",
      debit_percentage: "",
      monthly_unlimited: false,
      monthly_max_per_zone: "",
      monthly_days_per_zone: "",
      monthly_max: "",
      monthly_days: "",
      daily_unlimited: false,
      daily_max_per_zone: "",
      daily_days_per_zone: "",
      daily_max: "",
      daily_days: "",
      usage_interval: "",
      group_by: "NONE",
    });
    setScheduleErrors({});
    setSelectedSchedule(null);
  };

  const openCreateScheduleModal = () => {
    resetScheduleForm();
    setScheduleModalOpen(true);
  };

  const openEditScheduleModal = (schedule) => {
    setSelectedSchedule(schedule);
    setScheduleForm({
      weekday: schedule.weekday ?? "",
      start_time: schedule.start_time ?? "",
      end_time: schedule.end_time ?? "",
      debit_percentage: schedule.debit_percentage ?? "",
      monthly_unlimited: !!schedule.monthly_unlimited,
      monthly_max_per_zone: schedule.monthly_max_per_zone ?? "",
      monthly_days_per_zone: schedule.monthly_days_per_zone ?? "",
      monthly_max: schedule.monthly_max ?? "",
      monthly_days: schedule.monthly_days ?? "",
      daily_unlimited: !!schedule.daily_unlimited,
      daily_max_per_zone: schedule.daily_max_per_zone ?? "",
      daily_days_per_zone: schedule.daily_days_per_zone ?? "",
      daily_max: schedule.daily_max ?? "",
      daily_days: schedule.daily_days ?? "",
      usage_interval: schedule.usage_interval ?? "",
      group_by: schedule.group_by ?? "NONE",
    });
    setScheduleErrors({});
    setScheduleModalOpen(true);
  };

  const closeScheduleModal = () => {
    setScheduleModalOpen(false);
    resetScheduleForm();
  };

  // --- SAVE SCHEDULE (CREATE/UPDATE) ---

  const saveSchedule = async () => {
    if (!selectedEntry) {
      return;
    }

    setScheduleErrors({});

    const payload = {
      discount_entry_id: selectedEntry.id,
      weekday: schedulesUseWeekday
        ? scheduleForm.weekday === ""
          ? null
          : Number(scheduleForm.weekday)
        : null,
      start_time: scheduleForm.start_time,
      end_time: scheduleForm.end_time,
      debit_percentage:
        scheduleForm.debit_percentage === ""
          ? null
          : Number(scheduleForm.debit_percentage),

      monthly_unlimited: !!scheduleForm.monthly_unlimited,
      monthly_max_per_zone:
        scheduleForm.monthly_max_per_zone === ""
          ? null
          : Number(scheduleForm.monthly_max_per_zone),
      monthly_days_per_zone:
        scheduleForm.monthly_days_per_zone === ""
          ? null
          : Number(scheduleForm.monthly_days_per_zone),
      monthly_max:
        scheduleForm.monthly_max === ""
          ? null
          : Number(scheduleForm.monthly_max),
      monthly_days:
        scheduleForm.monthly_days === ""
          ? null
          : Number(scheduleForm.monthly_days),

      daily_unlimited: !!scheduleForm.daily_unlimited,
      daily_max_per_zone:
        scheduleForm.daily_max_per_zone === ""
          ? null
          : Number(scheduleForm.daily_max_per_zone),
      daily_days_per_zone:
        scheduleForm.daily_days_per_zone === ""
          ? null
          : Number(scheduleForm.daily_days_per_zone),
      daily_max:
        scheduleForm.daily_max === ""
          ? null
          : Number(scheduleForm.daily_max),
      daily_days:
        scheduleForm.daily_days === ""
          ? null
          : Number(scheduleForm.daily_days),

      usage_interval:
        scheduleForm.usage_interval === ""
          ? null
          : Number(scheduleForm.usage_interval),
      group_by: scheduleForm.group_by || "NONE",
    };

    try {
      const url = selectedSchedule
        ? `${schedulesControllerUrl}/${selectedSchedule.id}`
        : schedulesControllerUrl;
      const method = selectedSchedule ? "PUT" : "POST";

      const response = await callBackendApi(url, method, payload);

      if (response && response.success) {
        dispatch(
          showSnackbar({ message: "Horário guardado com sucesso!" })
        );
        closeScheduleModal();
        loadSchedules(selectedEntry.id);
      } else if (response && response.error) {
        setScheduleErrors(response.error);
        dispatch(
          showSnackbar({
            message: "Existem erros de validação no horário.",
          })
        );
      } else {
        dispatch(
          showSnackbar({
            message: "Ocorreu um erro ao guardar o horário.",
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: "Erro no pedido de horário." }));
    }
  };

  // --- DELETE SCHEDULE ---

  const deleteSchedule = async (scheduleId) => {
    try {
      const confirmDelete = window.confirm(
        "Tens a certeza que queres eliminar este horário?"
      );
      if (!confirmDelete) return;

      const response = await callBackendApi(
        `${schedulesControllerUrl}/${scheduleId}`,
        "DELETE"
      );

      if (response && response.success) {
        dispatch(showSnackbar({ message: "Horário eliminado com sucesso!" }));
        if (selectedEntry) {
          loadSchedules(selectedEntry.id);
        }
      } else {
        dispatch(
          showSnackbar({
            message: "Ocorreu um erro ao eliminar o horário.",
          })
        );
      }
    } catch (error) {
      console.error(error);
      dispatch(showSnackbar({ message: "Erro no pedido de horário." }));
    }
  };

  // --- SCHEDULE TABLE COLUMNS ---

  const scheduleColumns = [
    {
      field: "weekday",
      headerName: "Dia",
      width: 130,
      valueFormatter: (params) => {
        if(!params?.value) return ''
        if (params.value === null || params.value === undefined) {
          return "Todos os dias";
        }
        const found = weekdayOptions.find((d) => d.value === params.value);
        return found ? found.label : params.value;
      },
    },
    { field: "start_time", headerName: "Início", width: 90 },
    { field: "end_time", headerName: "Fim", width: 90 },
    {
      field: "debit_percentage",
      headerName: "Débito (%)",
      width: 110,
      valueFormatter: (params) =>
        params.value != null ? `${params.value}%` : "",
    },
    {
      field: "group_by",
      headerName: "Agrupar",
      width: 140,
      valueFormatter: (params) => {
        if (params.value === "BY_INVENTORY") return "Por inventário";
        return "Não agrupar";
      },
    },
    {
      field: "actions",
      headerName: "Ações",
      width: 130,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 1 }}>
          <IconButton
            onClick={() => openEditScheduleModal(params.row)}
            color="primary"
            size="small"
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => deleteSchedule(params.row.id)}
            color="error"
            size="small"
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  // --- MAIN RENDER ---

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: "1700px", margin: "auto" }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Button variant="contained" onClick={createEntryModal}>
            Criar Discount Entry
          </Button>
        </Grid>

        <Grid item xs={12}>
          <DataGrid
            rows={entries}
            columns={columns}
            pageSize={10}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            autoHeight
          />
        </Grid>
      </Grid>

      {/* -------- MODAL ENTRY -------- */}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {selectedEntry ? "Editar Discount Entry" : "Criar Discount Entry"}
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                error={Boolean(errors.description)}
                helperText={errors.description?.[0]}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Tipo de Dias"
                value={form.days_of_week}
                onChange={(e) =>
                  setForm({ ...form, days_of_week: e.target.value })
                }
                error={Boolean(errors.days_of_week)}
                helperText={errors.days_of_week?.[0]}
              >
                {daysOfWeekOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Modo de Trabalho"
                value={form.working_mode}
                onChange={(e) =>
                  setForm({ ...form, working_mode: e.target.value })
                }
                error={Boolean(errors.working_mode)}
                helperText={errors.working_mode?.[0]}
              >
                {workingModeOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Condição de Utilização"
                value={form.using_condition}
                onChange={(e) =>
                  setForm({ ...form, using_condition: e.target.value })
                }
                error={Boolean(errors.using_condition)}
                helperText={
                  errors.using_condition?.[0] || "TODO: ajustar opções reais"
                }
              >
                {usingConditionOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {requiresStayFields && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tempo Máx. de Permanência"
                    value={form.max_stay_time}
                    onChange={(e) =>
                      setForm({ ...form, max_stay_time: e.target.value })
                    }
                    error={Boolean(errors.max_stay_time)}
                    helperText={errors.max_stay_time?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tolerância Acesso Inadvertido"
                    value={form.inadverted_access_tolerance}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        inadverted_access_tolerance: e.target.value,
                      })
                    }
                    error={Boolean(errors.inadverted_access_tolerance)}
                    helperText={errors.inadverted_access_tolerance?.[0]}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Excesso"
                value={form.habilitate_excess}
                onChange={(e) =>
                  setForm({ ...form, habilitate_excess: e.target.value })
                }
                error={Boolean(errors.habilitate_excess)}
                helperText={errors.habilitate_excess?.[0]}
              >
                {habilitateExcessOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {requiresExcessDescription && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição do Excesso"
                  value={form.excess_description}
                  onChange={(e) =>
                    setForm({ ...form, excess_description: e.target.value })
                  }
                  error={Boolean(errors.excess_description)}
                  helperText={errors.excess_description?.[0]}
                />
              </Grid>
            )}

            {/* --- SCHEDULES SECTION (apenas em modo edição) --- */}
            {selectedEntry && (
              <Grid item xs={12} mt={3}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle1">
                    Horários desta Discount Entry
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={openCreateScheduleModal}
                  >
                    Adicionar horário
                  </Button>
                </Box>

                {schedulesLoading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <DataGrid
                    rows={schedules}
                    columns={scheduleColumns}
                    pageSize={5}
                    getRowId={(row) => row.id}
                    autoHeight
                    disableRowSelectionOnClick
                  />
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={saveEntry}>
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* -------- MODAL SCHEDULE -------- */}
      <Dialog
        open={scheduleModalOpen}
        onClose={closeScheduleModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedSchedule
            ? "Editar horário da Discount Entry"
            : "Adicionar horário à Discount Entry"}
        </DialogTitle>

        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {schedulesUseWeekday && (
              <Grid item xs={12} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Dia da semana"
                  value={scheduleForm.weekday}
                  onChange={(e) =>
                    setScheduleForm({
                      ...scheduleForm,
                      weekday: e.target.value,
                    })
                  }
                  error={Boolean(scheduleErrors.weekday)}
                  helperText={scheduleErrors.weekday?.[0]}
                >
                  {weekdayOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}

            {!schedulesUseWeekday && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Esta Discount Entry está configurada como "Sempre igual":
                  este horário aplica-se a todos os dias.
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="time"
                label="Hora de início"
                value={scheduleForm.start_time}
                onChange={(e) =>
                  setScheduleForm({
                    ...scheduleForm,
                    start_time: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                error={Boolean(scheduleErrors.start_time)}
                helperText={scheduleErrors.start_time?.[0]}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="time"
                label="Hora de fim"
                value={scheduleForm.end_time}
                onChange={(e) =>
                  setScheduleForm({
                    ...scheduleForm,
                    end_time: e.target.value,
                  })
                }
                InputLabelProps={{ shrink: true }}
                error={Boolean(scheduleErrors.end_time)}
                helperText={scheduleErrors.end_time?.[0]}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Débito (%)"
                value={scheduleForm.debit_percentage}
                onChange={(e) =>
                  setScheduleForm({
                    ...scheduleForm,
                    debit_percentage: e.target.value,
                  })
                }
                error={Boolean(scheduleErrors.debit_percentage)}
                helperText={scheduleErrors.debit_percentage?.[0]}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2">Máx. entradas / mês</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scheduleForm.monthly_unlimited}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        monthly_unlimited: e.target.checked,
                      })
                    }
                  />
                }
                label="Ilimitado"
              />
            </Grid>

            {!scheduleForm.monthly_unlimited && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="x p/zona"
                    value={scheduleForm.monthly_max_per_zone}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        monthly_max_per_zone: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.monthly_max_per_zone)}
                    helperText={scheduleErrors.monthly_max_per_zone?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="dias p/zona"
                    value={scheduleForm.monthly_days_per_zone}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        monthly_days_per_zone: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.monthly_days_per_zone)}
                    helperText={scheduleErrors.monthly_days_per_zone?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="x (total)"
                    value={scheduleForm.monthly_max}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        monthly_max: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.monthly_max)}
                    helperText={scheduleErrors.monthly_max?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="dias (total)"
                    value={scheduleForm.monthly_days}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        monthly_days: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.monthly_days)}
                    helperText={scheduleErrors.monthly_days?.[0]}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} mt={2}>
              <Typography variant="subtitle2">Máx. entradas / dia</Typography>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={scheduleForm.daily_unlimited}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        daily_unlimited: e.target.checked,
                      })
                    }
                  />
                }
                label="Ilimitado"
              />
            </Grid>

            {!scheduleForm.daily_unlimited && (
              <>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="x p/zona"
                    value={scheduleForm.daily_max_per_zone}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        daily_max_per_zone: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.daily_max_per_zone)}
                    helperText={scheduleErrors.daily_max_per_zone?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="dias p/zona"
                    value={scheduleForm.daily_days_per_zone}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        daily_days_per_zone: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.daily_days_per_zone)}
                    helperText={scheduleErrors.daily_days_per_zone?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="x (total)"
                    value={scheduleForm.daily_max}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        daily_max: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.daily_max)}
                    helperText={scheduleErrors.daily_max?.[0]}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    type="number"
                    label="dias (total)"
                    value={scheduleForm.daily_days}
                    onChange={(e) =>
                      setScheduleForm({
                        ...scheduleForm,
                        daily_days: e.target.value,
                      })
                    }
                    error={Boolean(scheduleErrors.daily_days)}
                    helperText={scheduleErrors.daily_days?.[0]}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} md={4} mt={2}>
              <TextField
                fullWidth
                type="number"
                label="Intervalo de utilização"
                value={scheduleForm.usage_interval}
                onChange={(e) =>
                  setScheduleForm({
                    ...scheduleForm,
                    usage_interval: e.target.value,
                  })
                }
                error={Boolean(scheduleErrors.usage_interval)}
                helperText={scheduleErrors.usage_interval?.[0]}
              />
            </Grid>

            <Grid item xs={12} md={4} mt={2}>
              <TextField
                select
                fullWidth
                label="Agrupar"
                value={scheduleForm.group_by}
                onChange={(e) =>
                  setScheduleForm({
                    ...scheduleForm,
                    group_by: e.target.value,
                  })
                }
                error={Boolean(scheduleErrors.group_by)}
                helperText={scheduleErrors.group_by?.[0]}
              >
                {groupByOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeScheduleModal}>Cancelar</Button>
          <Button variant="contained" onClick={saveSchedule}>
            Guardar horário
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DiscountEntries;
